import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, User, Mail, Building, Zap, Share2, Lock, Palette, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import apiClient from '../lib/apiClient';
import { energyService, googleAdsService } from '../services';
import Tooltip from '../ui/Tooltip/Tooltip';
import './Settings.css';

function Settings() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', address: '' });
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [energy, setEnergy] = useState(0);
  const [referralCode, setReferralCode] = useState('');
  const [referrals, setReferrals] = useState({ invited: 0, confirmed: 0, credits: 0 });
  const [googleAdsStatus, setGoogleAdsStatus] = useState({ connected: false, account: null });
  const [isGoogleAdsLoading, setIsGoogleAdsLoading] = useState(false);
  const [googleAdsMessage, setGoogleAdsMessage] = useState('');

  useEffect(() => {
    setForm((prev) => ({ ...prev, name: user?.name || '', email: user?.email || '' }));

    const loadData = async () => {
      try {
        const [energyData, referralsData, gaStatus] = await Promise.all([
          energyService.getEnergy(),
          apiClient.request('/user/referrals'),
          googleAdsService.getStatus()
        ]);

        if (energyData?.success) {
          setEnergy(energyData.energy || 0);
          setReferralCode(energyData.referral_code || '');
        }

        if (referralsData?.success) {
          setReferrals({
            invited: referralsData.referrals?.invited || 0,
            confirmed: referralsData.referrals?.confirmed || 0,
            credits: referralsData.referrals?.credits_earned || 0
          });
        }

        if (gaStatus?.success) {
          setGoogleAdsStatus({
            connected: Boolean(gaStatus.connected),
            account: gaStatus.account || null
          });
        }
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    };

    loadData();
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('google_ads');
    if (!status) return;

    if (status === 'connected') {
      setGoogleAdsMessage('Google Ads conectado com sucesso.');
    } else if (status === 'error') {
      const message = params.get('message') || 'Falha ao conectar Google Ads.';
      setGoogleAdsMessage(message);
    }
  }, []);

  const connectGoogleAds = async () => {
    setGoogleAdsMessage('');
    setIsGoogleAdsLoading(true);

    try {
      const response = await googleAdsService.getConnectUrl();
      if (!response?.success || !response?.auth_url) {
        setGoogleAdsMessage('Não foi possível iniciar a conexão com Google Ads.');
        return;
      }

      window.location.assign(response.auth_url);
    } catch (error) {
      console.error(error);
      setGoogleAdsMessage('Erro ao abrir autorização do Google Ads.');
    } finally {
      setIsGoogleAdsLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-grid">
        <section className="settings-card card">
          <h3>Conta</h3>
          <div className="input-group">
            <label><User size={14} /> Nome</label>
            <input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
          </div>
          <div className="input-group">
            <label><Mail size={14} /> E-mail</label>
            <input value={form.email} onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))} />
          </div>
          <div className="input-group">
            <label><Building size={14} /> Empresa</label>
            <input value={form.company} onChange={(event) => setForm((prev) => ({ ...prev, company: event.target.value }))} />
          </div>
          <div className="input-group">
            <label>Telefone</label>
            <input value={form.phone} onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))} />
          </div>
          <div className="input-group">
            <label>Endereço</label>
            <input value={form.address} onChange={(event) => setForm((prev) => ({ ...prev, address: event.target.value }))} />
          </div>
          <button className="btn btn-outline">
            <div className="blob1"></div>
            <div className="blob2"></div>
            <div className="inner"><Save size={16} /> Salvar</div>
          </button>
        </section>

        <section className="settings-card card">
          <Tooltip content="Acompanhe seu saldo de energia para planejar geração de conteúdos sem interrupções." variant="info" block>
            <h3><Zap size={16} /> Energia</h3>
          </Tooltip>
          <p>Saldo atual: <strong>{energy}</strong> créditos</p>
          <p>Código referral: <strong>{referralCode || '---'}</strong></p>
          <div className="settings-stats">
            <span>Convidados: {referrals.invited}</span>
            <span>Confirmados: {referrals.confirmed}</span>
            <span>Créditos ganhos: {referrals.credits}</span>
          </div>
          <button className="btn btn-secondary">
            <Share2 size={16} /> Compartilhar código
          </button>
        </section>

        <section className="settings-card card">
          <h3><Lock size={16} /> Segurança e Preferências</h3>

          <Tooltip content="Atualize sua senha regularmente para proteger sua conta." variant="warning" block>
            <div className="input-group">
              <label><Lock size={14} /> Senha atual</label>
              <input
                type="password"
                value={passwordForm.current}
                onChange={(event) => setPasswordForm((prev) => ({ ...prev, current: event.target.value }))}
              />
            </div>
          </Tooltip>

          <div className="input-group">
            <label>Nova senha</label>
            <input
              type="password"
              value={passwordForm.next}
              onChange={(event) => setPasswordForm((prev) => ({ ...prev, next: event.target.value }))}
            />
          </div>

          <div className="input-group">
            <label>Confirmar nova senha</label>
            <input
              type="password"
              value={passwordForm.confirm}
              onChange={(event) => setPasswordForm((prev) => ({ ...prev, confirm: event.target.value }))}
            />
          </div>

          <button className="btn btn-outline" type="button" onClick={() => setPasswordForm({ current: '', next: '', confirm: '' })}>
            <div className="blob1"></div>
            <div className="blob2"></div>
            <div className="inner"><Save size={16} /> Atualizar Senha</div>
          </button>

          <Tooltip content="Alterna o sistema entre modo claro e escuro automaticamente no layout inteiro." variant="tip" block>
            <button className="btn btn-secondary" type="button" onClick={toggleTheme}>
              <Palette size={16} /> Tema atual: {theme === 'dark' ? 'Escuro' : 'Claro'}
            </button>
          </Tooltip>

          <Tooltip content="Acesse os detalhes do seu plano e limites disponíveis para energia e recursos." variant="info" block>
            <button className="btn btn-secondary" type="button" onClick={() => navigate('/plans')}>
              <Crown size={16} /> Ver Plano Atual
            </button>
          </Tooltip>

          <Tooltip content="Conecte sua conta Google Ads para promover conteúdos gerados diretamente pela plataforma." variant="tip" block>
            <button className="btn btn-secondary" type="button" onClick={connectGoogleAds} disabled={isGoogleAdsLoading}>
              <Crown size={16} /> {isGoogleAdsLoading ? 'Conectando...' : googleAdsStatus.connected ? 'Google Ads Conectado' : 'Conectar Google Ads'}
            </button>
          </Tooltip>

          {googleAdsStatus.connected && (
            <p className="settings-googleads-status">
              Conta conectada: <strong>{googleAdsStatus.account?.google_ads_customer_id || 'sem customer_id'}</strong>
            </p>
          )}

          {googleAdsMessage && <p className="settings-googleads-status">{googleAdsMessage}</p>}
        </section>
      </div>
    </div>
  );
}

export default Settings;
